import { useState, useEffect, useCallback } from 'react';
import { apiClient, AccountFlags, DeviceHealth, SystemMetrics, QuotaUsage, SoakSummary, SoakTestResult } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { QuotaMeter } from '../components/StatusIndicators';
import { MetricCardSkeleton, CardSkeleton } from '../components/Skeleton';
import { toast } from '../components/Toast';

export function AdminPage() {
  const { accountId } = useAuth();
  const [flags, setFlags] = useState<AccountFlags>({
    transcription_mode: 'CLOUD',
    retention_days: '30',
    upload_only_on_wifi: 'true',
    speaker_verification_preset: 'STRICT',
    llm_enhancement_enabled: 'true',
  });
  const [devices, setDevices] = useState<DeviceHealth[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [quota, setQuota] = useState<QuotaUsage | null>(null);
  const [soakSummary, setSoakSummary] = useState<SoakSummary | null>(null);
  const [soakResults, setSoakResults] = useState<SoakTestResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Retention settings
  const [retentionDays, setRetentionDays] = useState(30);

  // Auto-refresh interval
  const [autoRefresh, setAutoRefresh] = useState(true);

  const loadData = useCallback(async () => {
    setError(null);

    try {
      // Load all data in parallel
      const [flagsResponse, devicesResponse, metricsResponse, quotaResponse, soakSummaryResponse, soakResultsResponse] = await Promise.all([
        apiClient.getFlags(),
        apiClient.getDeviceHealth(accountId || undefined),
        apiClient.getMetrics(),
        apiClient.getQuotaUsage(),
        apiClient.getSoakSummary(7),
        apiClient.getSoakResults({ limit: 5 }),
      ]);

      if (flagsResponse.success && flagsResponse.data) {
        setFlags(flagsResponse.data.flags);
        if (flagsResponse.data.flags.retention_days) {
          setRetentionDays(parseInt(flagsResponse.data.flags.retention_days, 10) || 30);
        }
      }

      if (devicesResponse.success && devicesResponse.data) {
        setDevices(devicesResponse.data.devices);
      }

      if (metricsResponse.success && metricsResponse.data) {
        setMetrics(metricsResponse.data.metrics);
      }

      if (quotaResponse.success && quotaResponse.data) {
        setQuota(quotaResponse.data.quota);
      }

      if (soakSummaryResponse.success && soakSummaryResponse.data) {
        setSoakSummary(soakSummaryResponse.data);
      }

      if (soakResultsResponse.success && soakResultsResponse.data) {
        setSoakResults(soakResultsResponse.data.results);
      }
    } catch {
      setError('Failed to load settings');
      toast.error('Failed to load admin data');
    }

    setIsLoading(false);
  }, [accountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh metrics every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      // Silently refresh metrics and quota
      Promise.all([apiClient.getMetrics(), apiClient.getQuotaUsage()]).then(
        ([metricsResp, quotaResp]) => {
          if (metricsResp.success && metricsResp.data) {
            setMetrics(metricsResp.data.metrics);
          }
          if (quotaResp.success && quotaResp.data) {
            setQuota(quotaResp.data.quota);
          }
        }
      );
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleFlagChange = (name: string, value: string) => {
    setFlags((prev) => ({ ...prev, [name]: value }));
  };

  const saveFlags = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.updateFlags({
        transcription_mode: flags.transcription_mode,
        retention_days: retentionDays.toString(),
        upload_only_on_wifi: flags.upload_only_on_wifi,
        speaker_verification_preset: flags.speaker_verification_preset,
        llm_enhancement_enabled: flags.llm_enhancement_enabled,
      });

      if (response.success) {
        setSuccess('Settings saved successfully');
        toast.success('Settings saved');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.error || 'Failed to save settings');
        toast.error('Failed to save settings');
      }
    } catch {
      setError('Failed to save settings');
      toast.error('Failed to save settings');
    }

    setIsSaving(false);
  };

  const formatLastSeen = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const getDeviceStatus = (device: DeviceHealth) => {
    const now = Date.now();
    const diff = now - device.last_seen;

    if (diff < 300000) return { status: 'online', color: '#4caf50' }; // 5 min
    if (diff < 3600000) return { status: 'idle', color: '#ff9800' }; // 1 hour
    return { status: 'offline', color: '#f44336' };
  };

  if (isLoading) {
    return (
      <div className="admin-page">
        <h1 className="page-title">Admin Settings</h1>

        {/* Metrics Skeleton */}
        <div className="metrics-grid">
          {[1, 2, 3, 4].map((i) => (
            <MetricCardSkeleton key={i} />
          ))}
        </div>

        {/* Cards Skeleton */}
        <CardSkeleton lines={4} />
        <CardSkeleton lines={3} />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">Admin Settings</h1>
        <label className="auto-refresh-toggle">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          <span>Auto-refresh</span>
        </label>
      </div>

      {error && <div className="card error-card">{error}</div>}
      {success && <div className="card success-card">{success}</div>}

      {/* System Metrics */}
      {metrics && (
        <div className="card">
          <h2 className="section-title">System Metrics</h2>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{metrics.active_devices}</span>
              <span className="metric-label">Active Devices</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{metrics.transcripts_today.toLocaleString()}</span>
              <span className="metric-label">Transcripts Today</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{metrics.avg_latency_ms.toFixed(0)}ms</span>
              <span className="metric-label">Avg Latency</span>
            </div>
            <div className="metric-card">
              <span
                className={`metric-value ${metrics.error_rate > 1 ? 'metric-warning' : ''}`}
              >
                {metrics.error_rate.toFixed(2)}%
              </span>
              <span className="metric-label">Error Rate</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{metrics.active_connections}</span>
              <span className="metric-label">Active Connections</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{metrics.transcripts_total.toLocaleString()}</span>
              <span className="metric-label">Total Transcripts</span>
            </div>
          </div>
        </div>
      )}

      {/* Quota Usage */}
      {quota && (
        <div className="card">
          <h2 className="section-title">Quota Usage</h2>
          <div className="quota-grid">
            <QuotaMeter
              label="Transcription"
              used={quota.transcription.used}
              limit={quota.transcription.limit}
              unit="requests"
            />
            <QuotaMeter
              label="API Requests"
              used={quota.api_requests.used}
              limit={quota.api_requests.limit}
              unit="requests"
            />
            <QuotaMeter
              label="LLM Enhancements"
              used={quota.llm_enhancements.used}
              limit={quota.llm_enhancements.limit}
              unit="/hour"
            />
          </div>
        </div>
      )}

      {/* Soak Test Summary */}
      {soakSummary && (
        <div className="card">
          <h2 className="section-title">Soak Test Summary (Last 7 Days)</h2>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-value">{soakSummary.total_tests}</span>
              <span className="metric-label">Total Tests</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${soakSummary.pass_rate < 80 ? 'metric-warning' : ''}`}>
                {soakSummary.pass_rate}%
              </span>
              <span className="metric-label">Pass Rate</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${soakSummary.metrics.avg_battery_drain_per_hour > 2 ? 'metric-warning' : ''}`}>
                {soakSummary.metrics.avg_battery_drain_per_hour.toFixed(2)}%
              </span>
              <span className="metric-label">Avg Battery Drain/hr</span>
            </div>
            <div className="metric-card">
              <span className="metric-value">{soakSummary.metrics.avg_heap_mb.toFixed(0)}MB</span>
              <span className="metric-label">Avg Heap Usage</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${soakSummary.metrics.total_audio_gaps > 0 ? 'metric-warning' : ''}`}>
                {soakSummary.metrics.total_audio_gaps}
              </span>
              <span className="metric-label">Audio Gaps</span>
            </div>
            <div className="metric-card">
              <span className={`metric-value ${soakSummary.metrics.total_crashes > 0 ? 'metric-warning' : ''}`}>
                {soakSummary.metrics.total_crashes}
              </span>
              <span className="metric-label">Crashes</span>
            </div>
          </div>
        </div>
      )}

      {/* Recent Soak Tests */}
      {soakResults.length > 0 && (
        <div className="card">
          <h2 className="section-title">Recent Soak Tests</h2>
          <div className="soak-test-list">
            <div className="soak-test-header">
              <span>Device</span>
              <span>Duration</span>
              <span>Battery</span>
              <span>Memory</span>
              <span>Status</span>
            </div>
            {soakResults.map((test) => (
              <div key={test.test_id} className="soak-test-row">
                <span className="soak-device">{test.device_id.slice(0, 8)}...</span>
                <span className="soak-duration">
                  {Math.round(test.duration_ms / (1000 * 60 * 60))}h
                </span>
                <span className={`soak-battery ${test.battery.drain_per_hour > 2 ? 'warning' : ''}`}>
                  {test.battery.drain_per_hour.toFixed(2)}%/hr
                </span>
                <span className="soak-memory">
                  {test.memory.avg_heap_mb.toFixed(0)}MB
                </span>
                <span className={`soak-status ${test.passed ? 'passed' : 'failed'}`}>
                  {test.passed ? 'PASSED' : 'FAILED'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Retention Settings */}
      <div className="card">
        <h2 className="section-title">Data Retention</h2>
        <div className="form-group">
          <label>TRANSCRIPT_RETENTION_DAYS</label>
          <p className="form-hint">
            Transcripts older than this will be automatically deleted. Default: 30 days.
          </p>
          <div className="retention-input">
            <input
              type="number"
              className="input"
              value={retentionDays}
              onChange={(e) => setRetentionDays(parseInt(e.target.value, 10) || 30)}
              min="1"
              max="365"
            />
            <span className="retention-unit">days</span>
          </div>
        </div>
      </div>

      {/* Transcription Mode */}
      <div className="card">
        <h2 className="section-title">Transcription Mode</h2>
        <div className="form-group">
          <label>TRANSCRIPTION_MODE</label>
          <p className="form-hint">Controls where audio is transcribed.</p>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="transcription_mode"
                value="OFF"
                checked={flags.transcription_mode === 'OFF'}
                onChange={(e) => handleFlagChange('transcription_mode', e.target.value)}
              />
              <span className="radio-label">
                <strong>OFF</strong>
                <span>No transcription - audio stored only</span>
              </span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="transcription_mode"
                value="CLOUD"
                checked={flags.transcription_mode === 'CLOUD'}
                onChange={(e) => handleFlagChange('transcription_mode', e.target.value)}
              />
              <span className="radio-label">
                <strong>CLOUD</strong>
                <span>Cloudflare Workers AI (Whisper) - higher accuracy</span>
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* LLM Enhancement */}
      <div className="card">
        <h2 className="section-title">LLM Enhancement</h2>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={flags.llm_enhancement_enabled === 'true'}
              onChange={(e) =>
                handleFlagChange('llm_enhancement_enabled', e.target.checked ? 'true' : 'false')
              }
            />
            <span>Enable LLM transcript enhancement</span>
          </label>
          <p className="form-hint">
            When enabled, transcripts can be enhanced using AI to fix grammar and remove filler
            words.
          </p>
        </div>
      </div>

      {/* Other Settings */}
      <div className="card">
        <h2 className="section-title">Upload Settings</h2>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={flags.upload_only_on_wifi === 'true'}
              onChange={(e) =>
                handleFlagChange('upload_only_on_wifi', e.target.checked ? 'true' : 'false')
              }
            />
            <span>Upload only on WiFi</span>
          </label>
          <p className="form-hint">When enabled, uploads will wait for WiFi connection.</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="card">
        <button className="button" onClick={saveFlags} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Device Health */}
      <div className="card">
        <h2 className="section-title">Device Health</h2>
        <p className="form-hint">Registered devices and their last ping times.</p>

        {devices.length === 0 ? (
          <div className="placeholder">
            <p>No devices registered yet.</p>
            <p>Devices will appear here once they connect to the service.</p>
          </div>
        ) : (
          <>
            {/* Device summary cards */}
            <div className="device-summary">
              <div className="device-summary-card online">
                <span className="device-summary-count">
                  {devices.filter((d) => getDeviceStatus(d).status === 'online').length}
                </span>
                <span className="device-summary-label">Online</span>
              </div>
              <div className="device-summary-card idle">
                <span className="device-summary-count">
                  {devices.filter((d) => getDeviceStatus(d).status === 'idle').length}
                </span>
                <span className="device-summary-label">Idle</span>
              </div>
              <div className="device-summary-card offline">
                <span className="device-summary-count">
                  {devices.filter((d) => getDeviceStatus(d).status === 'offline').length}
                </span>
                <span className="device-summary-label">Offline</span>
              </div>
            </div>

            <div className="device-list">
              <div className="device-header">
                <span>Device</span>
                <span>Version</span>
                <span>Battery</span>
                <span>Last Seen</span>
                <span>Status</span>
              </div>
              {devices.map((device) => {
                const status = getDeviceStatus(device);
                return (
                  <div key={device.device_id} className="device-row">
                    <span className="device-id">{device.device_id.slice(0, 8)}...</span>
                    <span className="device-version">
                      {device.app_version} / {device.os_version}
                    </span>
                    <span className="device-battery">
                      {device.battery_level !== null ? (
                        <span
                          className={`battery-level ${device.battery_level < 20 ? 'low' : ''}`}
                        >
                          {device.battery_level}%
                        </span>
                      ) : (
                        '-'
                      )}
                    </span>
                    <span className="device-last-seen">{formatLastSeen(device.last_seen)}</span>
                    <span className="device-status" style={{ color: status.color }}>
                      {status.status}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <button className="button button-secondary refresh-btn" onClick={loadData}>
          Refresh
        </button>
      </div>
    </div>
  );
}
